import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { config } from "../configs";

const BASE_URL = config.REACT_APP_BASE_URL ?? "";

export const CONVERSATIONS_PAGE_SIZE = 20;
export const HISTORY_PAGE_SIZE = 20;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Conversation {
  id: string;
  title: string;
  updatedAt: string;
}

interface ConversationRaw {
  _id: string;
  title: string;
  updatedAt: string;
}

export interface ConversationsArg {
  search?: string;
  page: number;
}

export interface ConversationsPage {
  items: Conversation[];
  hasMore: boolean;
}

export interface HistoryItem {
  id: string;
  question: string;
  answer: string;
  rating: string | null;
  createdAt: string;
}

interface HistoryItemRaw {
  _id: string;
  question: string;
  answer: string;
  rating: string | null;
  createdAt: string;
}

export interface HistoryArg {
  conversationId: string;
  page: number;
}

export interface HistoryPage {
  items: HistoryItem[];
  hasMore: boolean;
}
interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

export interface StreamCallbacks {
  onDelta: (text: string) => void;
  onDone: (interactionId: string) => void;
  onError: (message: string) => void;
}

// ─── RTK Query API ────────────────────────────────────────────────────────────

export const chatbotApi = createApi({
  reducerPath: "chatbotApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_URL}/api/user`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ["History"],
  endpoints: (builder) => ({
    // Paginated infinite scroll: pages of one search merge into a single cache
    // entry. No tag invalidation — refreshes are driven by the component.
    getConversations: builder.query<ConversationsPage, ConversationsArg>({
      query: ({ search, page }) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("limit", String(CONVERSATIONS_PAGE_SIZE));
        const trimmed = search?.trim();
        if (trimmed) params.set("search", trimmed);
        return `conversations?${params.toString()}`;
      },
      transformResponse: (
        res: ApiResponse<{ data: ConversationRaw[]; hasMore: boolean }>
      ) => ({
        items: res.data.data.map(({ _id, ...rest }) => ({ id: _id, ...rest })),
        hasMore: res.data.hasMore,
      }),
      // Cache key ignores page, so all pages share one entry.
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}:${queryArgs.search?.trim() ?? ""}`,
      merge: (currentCache, incoming, { arg }) => {
        currentCache.hasMore = incoming.hasMore;
        // page 1 replaces (fresh/refresh), later pages append.
        if (arg.page <= 1) currentCache.items = incoming.items;
        else currentCache.items.push(...incoming.items);
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page,
    }),
    createConversation: builder.mutation<Conversation, void>({
      query: () => ({ url: "conversations", method: "POST" }),
      transformResponse: (res: ApiResponse<ConversationRaw>) => ({
        id: res.data._id,
        title: res.data.title,
        updatedAt: res.data.updatedAt,
      }),
    }),
    deleteConversation: builder.mutation<void, string>({
      query: (id) => ({
        url: `conversations?id=${encodeURIComponent(id)}`,
        method: "DELETE",
      }),
    }),
    // Paginated: page 1 is the most recent messages (oldest-first); later pages
    // are older batches. Kept per-page (no merge) — the component prepends each
    // page into its own message list.
    getHistory: builder.query<HistoryPage, HistoryArg>({
      query: ({ conversationId, page }) => {
        const params = new URLSearchParams();
        if (conversationId) params.set("conversationId", conversationId);
        params.set("page", String(page));
        params.set("limit", String(HISTORY_PAGE_SIZE));
        return `chat/history?${params.toString()}`;
      },
      transformResponse: (
        res: ApiResponse<{ data: HistoryItemRaw[]; hasMore: boolean }>
      ) => ({
        items: res.data.data.map(({ _id, ...rest }) => ({ id: _id, ...rest })),
        hasMore: res.data.hasMore,
      }),
      providesTags: (_result, _error, { conversationId }) => [
        { type: "History" as const, id: conversationId },
      ],
    }),
    submitFeedback: builder.mutation<
      void,
      {
        interactionId: string;
        rating: "thumbs_up" | "thumbs_down";
        feedbackText?: string | null;
      }
    >({
      query: ({ interactionId, rating, feedbackText }) => ({
        url: `interactions/${interactionId}/feedback`,
        method: "POST",
        body: { rating, feedbackText },
      }),
    }),
    submitEscalation: builder.mutation<
      void,
      {
        name: string;
        email: string;
        question: string;
        conversationId: string | null;
      }
    >({
      query: (body) => ({ url: "escalations", method: "POST", body }),
    }),
  }),
});

export const {
  useGetConversationsQuery,
  useCreateConversationMutation,
  useDeleteConversationMutation,
  useGetHistoryQuery,
  useSubmitFeedbackMutation,
  useSubmitEscalationMutation,
} = chatbotApi;

// ─── Chat (streaming SSE) — kept as plain fetch; RTK Query has no SSE support ──

export async function sendMessage(
  message: string,
  conversationId: string,
  callbacks: StreamCallbacks,
  signal?: AbortSignal
): Promise<void> {
  const token = localStorage.getItem("token");
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${BASE_URL}/api/user/chat`, {
    method: "POST",
    headers,
    body: JSON.stringify({ message, conversationId }),
    signal,
  });

  if (!res.ok) {
    callbacks.onError(`Request failed (${res.status})`);
    return;
  }

  const reader = res.body?.getReader();
  if (!reader) {
    callbacks.onError("Streaming not available");
    return;
  }

  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        try {
          const evt = JSON.parse(line.slice(6));
          if (evt.type === "delta") callbacks.onDelta(evt.text);
          else if (evt.type === "done") callbacks.onDone(evt.interactionId);
          else if (evt.type === "error") callbacks.onError(evt.error);
        } catch {
          // skip malformed lines
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
